import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import strings from "global/constants/StringConstants";
import { maskedContent } from "helpers/maskedContent";
import { ChangeCardModel, CompareSegment } from "../ContractCompareTypesAndValidation";
import styles from "../ContractCompare.styles";
import t from "../ContractCompare.tokens";
import { BlankGlyph, CodeGlyph, MinusGlyph, PencilGlyph, PlusGlyph, SwapGlyph } from "./ContractCompareIcons";

const TAG_ICON = { width: t.result.card.tag.icon, height: t.result.card.tag.icon };

const TYPE_ICONS: Record<ChangeCardModel["type"], ReactNode> = {
  changed: <PencilGlyph sx={TAG_ICON} />,
  added: <PlusGlyph sx={TAG_ICON} />,
  removed: <MinusGlyph sx={TAG_ICON} />,
  moved: <SwapGlyph sx={TAG_ICON} />,
  filled: <BlankGlyph sx={TAG_ICON} />,
  other: null,
};

const page = strings.contractCompare.page;

/**
 * The clause with its marks. Struck and underlined words are never the only signal: a screen reader hears a visually
 * hidden "removed:" / "added:" before them, and the card carries the type tag too. The text is shown as text only.
 */
const Redline = ({ card }: { card: ChangeCardModel }) => {
  const segments: CompareSegment[] = card.segments.length
    ? card.segments
    : [{ op: "same", text: card.after?.text ?? card.before?.text ?? "" }];
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.op === "del") {
          return (
            <Box component="del" key={index} sx={styles.del}>
              <Box component="span" sx={styles.srOnly}>
                {page.srRemoved}{" "}
              </Box>
              {segment.text}
            </Box>
          );
        }
        if (segment.op === "ins") {
          return (
            <Box component="ins" key={index} sx={styles.ins}>
              <Box component="span" sx={styles.srOnly}>
                {page.srAdded}{" "}
              </Box>
              {segment.text}
            </Box>
          );
        }
        return <Fragment key={index}>{segment.text}</Fragment>;
      })}
    </>
  );
};

interface ChangeCardProps {
  card: ChangeCardModel;
  index: number;
}

/** One change: its type, clause heading and location, the one-sentence summary, and the quoted clause with marks. */
const ChangeCard = ({ card, index }: ChangeCardProps) => {
  const quoteRef = useRef<HTMLQuoteElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const quoteId = `compare-quote-${index}`;
  const shortened = !!(card.before?.truncated || card.after?.truncated);

  // A clause past six lines collapses; the control shows only when something is actually hidden.
  useEffect(() => {
    const el = quoteRef.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [expanded, card]);

  return (
    <Box
      component="li"
      id={`compare-card-${index}`}
      tabIndex={-1}
      sx={styles.card}
      {...maskedContent}
    >
      <Box sx={styles.cardTop}>
        <Box component="span" sx={styles.tag(card.type)}>
          {TYPE_ICONS[card.type]}
          {card.typeLabel}
        </Box>
        {card.heading && (
          <Box component="h3" dir="auto" sx={styles.cardHeading}>
            {card.heading}
          </Box>
        )}
        {card.location && (
          <Box component="span" sx={styles.location}>
            {card.location}
          </Box>
        )}
      </Box>

      <Box sx={styles.sentenceRow}>
        <Box component="p" sx={styles.sentence}>
          {card.sentence}
        </Box>
        {card.byCode && (
          <Box component="span" sx={styles.codeTag}>
            <CodeGlyph sx={{ width: t.result.card.codeTag.icon, height: t.result.card.codeTag.icon }} />
            {strings.contractCompare.sentenceByCode}
          </Box>
        )}
      </Box>

      <Box
        component="blockquote"
        id={quoteId}
        ref={quoteRef}
        dir="auto"
        sx={styles.quote(expanded)}
      >
        <Redline card={card} />
      </Box>
      {shortened && (
        <Box component="p" sx={styles.quoteNote}>
          {page.shortened}
        </Box>
      )}
      {(overflowing || expanded) && (
        <Box
          component="button"
          type="button"
          sx={styles.linkButton}
          aria-expanded={expanded}
          aria-controls={quoteId}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? page.showLess : page.showFull}
        </Box>
      )}
    </Box>
  );
};

export default ChangeCard;
